import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "helpers";

import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { NoResults } from "ui/components/NoResults";

import { IntegrationSearchPageCmp, IntegrationSearchPageProps } from "./types";
import { IntegrationCard } from "./components/IntegrationCard";
import { IntegrationGroupFilter } from "./components/IntegrationGroupFilter";

import { Routes } from "../../navigation";
import { INTEGRATIONS, INTEGRATION_GROUPS } from "../../consts";
import { Integration, IntegrationGroup, IntegrationStatus } from "../../types";
import {
  getInstalledIntegrationsAPI,
  installIntegrationAPI,
  uninstallIntegrationAPI,
} from "services/api/settings/integrations/api";

const INTEGRATION_PAGE_TITLE = {
  text: "admin.sidebar.integration",
  icon: "/media/icons/integration.svg",
  iconColor: "stroke-[#edeef8]",
};

const IntegrationSearchPage: IntegrationSearchPageCmp = (
  props: IntegrationSearchPageProps
) => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  // State for selected group filter
  const [selectedGroup, setSelectedGroup] = useState<IntegrationGroup | null>(null);

  // Katalog integracija je statičan (INTEGRATIONS), a status dolazi iz baze —
  // instalirane integracije su vezane uz tenanta i otvaraju pripadni oglasnik.
  const [installedIds, setInstalledIds] = useState<string[]>([]);

  const fetchInstalled = useCallback(async () => {
    try {
      const results = await getInstalledIntegrationsAPI();
      setInstalledIds(results);
    } catch {
      setInstalledIds([]);
    }
  }, []);

  useEffect(() => {
    fetchInstalled();
  }, [fetchInstalled]);

  const integrations = useMemo<Integration[]>(
    () =>
      INTEGRATIONS.map((integration) => ({
        ...integration,
        status: installedIds.includes(integration.id)
          ? IntegrationStatus.INSTALLED
          : IntegrationStatus.NOT_INSTALLED,
      })),
    [installedIds]
  );

  // Calculate integration counts per group
  const integrationCounts = useMemo(() => {
    const counts: Record<IntegrationGroup, number> = {
      [IntegrationGroup.OGLASNICI]: 0,
      [IntegrationGroup.FINANCIJE]: 0,
      [IntegrationGroup.OSIGURANJA]: 0,
    };

    integrations.forEach((integration) => {
      counts[integration.group]++;
    });

    return counts;
  }, [integrations]);

  // Filter integrations based on selected group
  const filteredIntegrations = useMemo(() => {
    if (selectedGroup === null) {
      return integrations;
    }
    return integrations.filter((integration) => integration.group === selectedGroup);
  }, [integrations, selectedGroup]);

  // Handle group selection
  const handleGroupSelect = useCallback((group: IntegrationGroup | null) => {
    setSelectedGroup(group);
  }, []);

  // Handle configure button click
  const handleConfigure = useCallback(
    (integrationId: string) => {
      navigate(`/integration/${integrationId}/settings`);
    },
    [navigate]
  );

  // Instalacija se pamti na tenantu — tek tad je oglasnik ponuđen pri unosu oglasa.
  const handleInstall = useCallback(
    async (integrationId: string) => {
      try {
        await installIntegrationAPI(integrationId);
        await fetchInstalled();
      } catch {
        return;
      }

      navigate(`/integration/${integrationId}/settings`);
    },
    [navigate, fetchInstalled]
  );

  const handleUninstall = useCallback(
    async (integrationId: string) => {
      try {
        await uninstallIntegrationAPI(integrationId);
        await fetchInstalled();
      } catch {
        // greške rukuje createApiCall (snackbar)
      }
    },
    [fetchInstalled]
  );



  return (
    <div className="">
      <div className="bg-white rounded-t-md border border-b mb-2">
        <PageHeader>
          <PageTitle
            title={INTEGRATION_PAGE_TITLE.text}
            icon={INTEGRATION_PAGE_TITLE.icon}
            iconColor={INTEGRATION_PAGE_TITLE.iconColor}
          />
          <Breadcrumb  />
        </PageHeader>
      </div>

      <div className="px-4 py-4">
        <div className="flex gap-6">
          {/* Sidebar with filters */}
          <div className="max-w-sm flex-1">
            <IntegrationGroupFilter
              groups={INTEGRATION_GROUPS}
              selectedGroup={selectedGroup}
              onGroupSelect={handleGroupSelect}
              integrationCounts={integrationCounts}
            />
          </div>

          {/* Main content area */}
          <div className="flex-1 max-w-6xl mx-auto">
            {/* Results header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-slate-800">
                {selectedGroup
                  ? t(`admin.integration.group.${selectedGroup}`)
                  : t("admin.integration.filter.all")}
                <span className="ml-2 text-sm font-normal text-slate-500">
                  ({filteredIntegrations.length})
                </span>
              </h2>
            </div>

            {/* Integration cards grid */}
            {filteredIntegrations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredIntegrations.map((integration) => (
                  <IntegrationCard
                    key={integration.id}
                    integration={integration}
                    onConfigure={handleConfigure}
                    onInstall={handleInstall}
                    onUninstall={handleUninstall}
                  />
                ))}
              </div>
            ) : (
              <NoResults title={t("admin.integration.noresults")} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

IntegrationSearchPage.route = Routes.IntegrationSearchPageRouter;

export default IntegrationSearchPage;
